import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Users, UserPlus, Search, X, Phone, Mail } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

export default function ManageCustomers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "Customer@123" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async (q = "") => {
    setLoading(true);
    try {
      const params = { role: "customer" };
      if (q) params.search = q;
      const res = await api.get("/users", { params });
      setCustomers(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers(search);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, role: "customer" });
      toast.success("Customer created successfully!");
      setShowAdd(false);
      setForm({ name: "", email: "", phone: "", password: "Customer@123" });
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create customer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Customers</h1>
            <p className="text-slate-500 text-sm">{customers.length} customer{customers.length !== 1 ? "s" : ""} found</p>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-customer-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase">
            <UserPlus className="w-4 h-4" /> Add Customer
          </button>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email, phone..."
              data-testid="customer-search"
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
          </div>
          <button type="submit" className="px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm">Search</button>
          {search && <button type="button" onClick={() => { setSearch(""); fetchCustomers(); }} className="px-3 py-2.5 border border-slate-200 text-slate-500 rounded-sm"><X className="w-4 h-4" /></button>}
        </form>

        {/* List */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading customers...</div>
          ) : customers.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No customers found.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {customers.map((c) => (
                <div key={c.id} data-testid="customer-row" onClick={() => ["admin", "staff"].includes(user?.role) && navigate(`${user.role === "admin" ? "/admin" : "/staff"}/customers/${c.id}`)} title="Open full customer view" className="flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {c.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-slate-800">{c.name}{c.customer_code && <span className="ml-2 font-mono text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{c.customer_code}</span>}</p>
                      <p className="text-xs text-slate-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {c.phone && <p className="text-xs text-slate-500 flex items-center gap-1 justify-end"><Phone className="w-3 h-3" /> {c.phone}</p>}
                    <span className={`text-xs font-semibold ${c.is_active ? "text-emerald-600" : "text-red-500"}`}>
                      {c.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Customer Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg text-slate-800 uppercase">Add Customer</h3>
              <button onClick={() => setShowAdd(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              {[
                { name: "name", label: "Full Name", placeholder: "Customer's name", required: true },
                { name: "email", label: "Email", placeholder: "email@example.com", type: "email", required: true },
                { name: "phone", label: "Phone", placeholder: "+91 9876543210" },
                { name: "password", label: "Initial Password", placeholder: "Password", required: true },
              ].map((f) => (
                <div key={f.name}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                  <input type={f.type || "text"} name={f.name} value={form[f.name]} required={f.required}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder} data-testid={`add-customer-${f.name}`}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)}
                  className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm">Cancel</button>
                <button type="submit" disabled={saving} data-testid="add-customer-submit"
                  className="flex-1 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70">
                  {saving ? "Creating..." : "Create Customer"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
